import React from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import { SkillCategoryIcon } from 'utils/skillCategoryIcons';
import { getLocalizedPath } from 'helpers/getLocale';
import { getSkillSlug } from 'utils/seo';
import professionCases from 'helpers/professionCases';

const Wrapper = styled('div')(({ theme, slides }) => ({
  flex: slides === 'true' ? 0 : 1,
  borderRadius: 15,
  background: 'linear-gradient(180deg, #74bf14 0%, #68a718 100%)',
  [theme.breakpoints.down('md')]: {
    marginBottom: 20
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.white.main}`,
  color: theme.palette.white.main,
  padding: '10px 25px',
  [theme.breakpoints.down('md')]: {
    lineHeight: '34px'
  },
  [theme.breakpoints.down('sm')]: {
    lineHeight: '34px',
    fontSize: 24,
    padding: '16px 25px'
  }
}));

const SkillButtonsWrapper = styled('div')(() => ({
  padding: '18px 25px',
  gap: '10px',
  display: 'flex',
  flexWrap: 'wrap'
}));

const SkillButton = styled(Button)(({ theme }) => ({
  color: theme.palette.white.main,
  borderColor: theme.palette.white.main,
  borderWidth: '1px',
  borderRadius: 10,
  padding: '6px 18px',
  fontSize: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '&:hover': {
    borderColor: theme.palette.white.main,
    color: theme.palette.black.main,
    backgroundColor: theme.palette.white.main,
    '& svg': {
      filter: 'brightness(0)'
    }
  },
  [theme.breakpoints.down('sm')]: {
    textAlign: 'left'
  }
}));

const CategoryIconWrapper = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  '& svg': {
    filter: 'brightness(0) invert(1)',
    fill: 'none !important',
    color: theme.palette.white.main,
    stroke: theme.palette.white.main,
    '& path, & circle, & rect, & polygon, & line': {
      fill: 'none !important',
      stroke: theme.palette.white.main
    }
  }
}));

const SkillsComponent = ({ professionInfo, t, slides }) => {
  const skills = professionInfo?.skills || [];

  // Hide component if no skills
  if (!skills.length) {
    return null;
  }

  // Get skill URL. getSkillSlug reads the current locale and picks the
  // matching name field, falling back through en → uk.
  const getSkillUrl = (skill) => getLocalizedPath(`/skills/${getSkillSlug(skill)}/${skill._id}`);

  return (
    <Wrapper slides={`${slides}`}>
      <Title variant={'h2'}>
        {t('MainSkillsTitle', professionCases(professionInfo, 'genitive'))}
      </Title>

      <SkillButtonsWrapper>
        {skills.map((skill) => {
          return (
            <SkillButton
              component={Link}
              to={getSkillUrl(skill)}
              key={skill?._id}
              variant="outlined">
              <CategoryIconWrapper>
                <SkillCategoryIcon skill={skill} size={20} />
              </CategoryIconWrapper>
              {skill.name}
            </SkillButton>
          );
        })}
      </SkillButtonsWrapper>
    </Wrapper>
  );
};

export default SkillsComponent;
